/**
 * Excel Utility Functions for Flood MIS District Datewise Report
 * Date: 2026-01-21
 * Following Heatwave Pattern
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format date to DD-MM-YYYY
 */
export const formatDate = (date) => {
  const formattedDate = new Date(date);
  const day = String(formattedDate.getDate()).padStart(2, '0');
  const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
  const year = formattedDate.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Get current timestamp in IST format for filename
 */
export const getTimestamp = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 330);
  const istDate = now.toISOString().split('T')[0];
  const istTime = now
    .toISOString()
    .split('T')[1]
    .split('.')[0]
    .replace(/:/g, '');
  return `${istDate}_${istTime}`;
};

/**
 * Format date for MIS Excel (handles epoch milliseconds)
 * Returns DD-MM-YYYY format
 */
export const formatDateForMISExcel = (dateValue) => {
  if (!dateValue) return '';

  try {
    let date;

    if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        // YYYY-MM-DD format
        const [year, month, day] = dateValue.split('-');
        return `${day}-${month}-${year}`;
      }
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number' && dateValue > 10000000000) {
      // Epoch milliseconds
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      // Epoch seconds
      date = new Date(dateValue * 1000);
    }

    if (!date || isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error('Date formatting error:', e);
    return String(dateValue);
  }
};

// ============================================================================
// FIELD MAPPING - Complete 124 columns for District Datewise Report
// ============================================================================

const FIELD_MAPPING = {
  // Row identification
  'date': 'Date',
  'block_name': 'Block Name',

  // Infrastructure (Cumulative) - 7 columns
  'totl_grm_pnchyt': 'Rural area: Total number of Gram Panchayats affected so far (Cumulative)',
  'nos_vilge_aftd': 'Rural area: Number of villages affected so far (Cumulative)',
  'totl_ngr_pnchyt': 'Urban Area: Total number of Nagar Panchayats affected so far (Cumulative)',
  'totl_mucpl_corp': 'Urban Area: Total affected Municipal corporation (Cumulative)',
  'totl_ngr_parsad': 'Urban Area: Total number of affected Nagar Parishad (Cumulative)',
  'nos_wrd_aftd': 'Urban Area: Number of wards affected so far (Cumulative)',
  'totl_aftd': 'Urban Area: Total',

  // Affected Population - Today (3 columns)
  'aftd_pop_rural_today': 'Affected population in Rural area Today (Newly affected)',
  'aftd_pop_urban_today': 'Affected population in Urban area Today (Newly affected)',
  'aftd_pop_totl_today': 'Affected population in Total Today (Newly affected)',

  // Affected Population - Until Yesterday (3 columns)
  'aftd_pop_rural_ystd': 'Affected population in Rural area Until Yesterday',
  'aftd_pop_urban_ystd': 'Affected population in Urban area Until Yesterday',
  'aftd_pop_totl_ystd': 'Affected population in Total Until Yesterday',

  // Affected Population - Cumulative (3 columns)
  'aftd_pop_rural': 'Affected population in Rural area so far (Cumulative)',
  'aftd_pop_urban': 'Affected population in Urban area so far (Cumulative)',
  'totl_pop_aftd': 'Affected population in Total so far (Cumulative)',

  // Families - Today (3 columns)
  'aftd_fam_rural_today': 'Number of families affected in Rural area Today (Newly affected)',
  'aftd_fam_urban_today': 'Number of families affected in Urban area Today (Newly affected)',
  'aftd_fam_totl_today': 'Number of families affected in Total Today (Newly affected)',

  // Families - Until Yesterday (3 columns)
  'aftd_fam_rural_ystd': 'Number of families affected in Rural area Until Yesterday',
  'aftd_fam_urban_ystd': 'Number of families affected in Urban area Until Yesterday',
  'aftd_fam_totl_ystd': 'Number of families affected in Total Until Yesterday',

  // Families - Cumulative (3 columns)
  'aftd_fam_rural': 'Number of families affected in Rural area so far (Cumulative)',
  'aftd_fam_urban': 'Number of families affected in Urban area so far (Cumulative)',
  'totl_fam_aftd': 'Number of families affected in Total so far (Cumulative)',

  // Animals - Today (3 columns)
  'aftd_anml_rural_today': 'Number of animals affected in Rural Today (Newly affected)',
  'aftd_anml_urban_today': 'Number of animals affected in Urban Today (Newly affected)',
  'aftd_anml_totl_today': 'Number of animals affected in Total Today (Newly affected)',

  // Animals - Until Yesterday (3 columns)
  'aftd_anml_rural_ystd': 'Number of animals affected in Rural area Until Yesterday',
  'aftd_anml_urban_ystd': 'Number of animals affected in Urban area Until Yesterday',
  'aftd_anml_totl_ystd': 'Number of animals affected in Total Until Yesterday',

  // Animals - Cumulative (3 columns)
  'aftd_anml_rural': 'Number of animals affected in Rural area so far (Cumulative)',
  'aftd_anml_urban': 'Number of animals affected in Urban area so far (Cumulative)',
  'totl_anml_aftd': 'Number of animals affected in Total so far (Cumulative)',

  // Geographic/Current Status (7 columns)
  'river_name': 'Name of major river which caused flood situation',
  'affected_blocks_count': 'How many blocks are currently affected by floods?',
  'affected_gram_panchayats': 'How many gram panchayats are currently affected by floods?',
  'affected_municipal_bodies': 'Number of municipal bodies (Nagar Panchayat + Municipal Council + Municipal Corporation) currently affected',
  'affected_villages': 'How many villages are currently affected by floods?',
  'affected_wards': 'How many wards are currently affected by floods?',
  'affected_population': 'How many population are currently affected by floods? (Rural area + Urban area)',

  // Area Affected (4 columns)
  'agricultural_area_affected_curr': 'Agricultural area affected in hectares on reporting date',
  'agricultural_area_affected_till_now': 'Agricultural area affected in hectares till now (cumulative)',
  'non_agricultural_area_affected_curr': 'Non-agricultural area affected in hectares on reporting date',
  'non_agricultural_area_affected_till_now': 'Non-agricultural area affected in hectares till now (cumulative)',

  // Crop Damage (4 columns)
  'damaged_crop_area_affected_curr': 'Damaged crop area in hectares on reporting date',
  'damaged_crop_area_affected_till_now': 'Damaged crop area in hectares till now (cumulative)',
  'estimated_value_of_damaged_crops_affected_curr': 'Estimated value of damaged crops in Rs Lakhs on reporting date',
  'estimated_value_of_damaged_crops_affected_till_now': 'Estimated value of damaged crops in Rs Lakhs till now (cumulative)',

  // Houses - Kutcha (4 columns)
  'ktha_cmplt_today': 'Number of Kutcha houses completely damaged on reporting date',
  'ktha_cmplt': 'Number of Kutcha houses completely damaged till now (cumulative)',
  'ktha_prtl_today': 'Number of Kutcha houses partially damaged on reporting date',
  'ktha_prtl': 'Number of Kutcha houses partially damaged till now (cumulative)',

  // Houses - Concrete (4 columns)
  'crct_cmplt_today': 'Number of Concrete houses completely damaged on reporting date',
  'crct_cmplt': 'Number of Concrete houses completely damaged till now (cumulative)',
  'crct_prtl_today': 'Number of Concrete houses partially damaged on reporting date',
  'crct_prtl': 'Number of Concrete houses partially damaged till now (cumulative)',

  // Houses - Cottage/Huts (2 columns)
  'ctg_hut_today': 'Number of Cottage/Huts damaged on reporting date',
  'ctg_hut': 'Number of Cottage/Huts damaged till now (cumulative)',

  // Houses - Animal Sheds (2 columns)
  'anml_shd_today': 'Number of Animal sheds damaged on reporting date',
  'anml_shd': 'Number of Animal sheds damaged till now (cumulative)',

  // House Values (4 columns)
  'estd_hse_val_today': 'Estimated value of damaged Houses in Rs lakh on the reporting date',
  'estd_hse_val': 'Estimated value of damaged Houses in Rs lakh till now (Cumulative)',
  'estd_pub_prty_val_today': 'Estimated value of damaged Public property in Rs lakh on the reporting date',
  'estd_pub_prty_val': 'Estimated value of damaged Public property in Rs lakh till now (Cumulative)',

  // Boats (4 columns)
  'damaged_boats_complete_curr': 'Number of completely damaged boats on reporting date',
  'damaged_boats_complete_till_now': 'Number of completely damaged boats till now (cumulative)',
  'damaged_boats_partial_curr': 'Number of partially damaged boats on reporting date',
  'damaged_boats_partial_till_now': 'Number of partially damaged boats till now (cumulative)',

  // Nets (4 columns)
  'damaged_net_complete_curr': 'Number of completely damaged nets on reporting date',
  'damaged_net_complete_till_now': 'Number of completely damaged nets till now (cumulative)',
  'damaged_net_partial_curr': 'Number of partially damaged nets on reporting date',
  'damaged_net_partial_till_now': 'Number of partially damaged nets till now (cumulative)',

  // Fish Farms (2 columns)
  'area_of_damaged_fish_egg_farm_curr': 'Area of damaged fish egg farm in hectares on the reporting date',
  'area_of_damaged_fish_egg_farm_till_now': 'Area of damaged fish egg farm in hectares till now (Cumulative)',

  // Deaths (4 columns)
  'human_deaths_curr': 'Human death count on reporting date',
  'human_deaths_till_now': 'Human death count till now (cumulative)',
  'animal_deaths_curr': 'Animal death count on reporting date',
  'animal_deaths_till_now': 'Animal death count till now (cumulative)',

  // Motorboats (3 columns)
  'mtr_bot': 'Number of motorboats operated/used',
  'govt_bot': 'Number of Government boats operated/used',
  'pvt_bot': 'Number of Private boats operated/used',

  // Evacuation (3 columns)
  'totl_evctd_till_ystd': 'Total number of Population evacuated till yesterday (A)',
  'totl_evctd_today': 'Total number of Population evacuated today (B)',
  'totl_evctd_till_now': 'Total number of Population evacuated till now (A+B)',

  // Relief Camps (8 columns)
  'totl_cmp_opn': 'Total number of Flood Relief Camps opened so far (Cumulative) (I)',
  'totl_cmp_cls': 'Total number of Flood Relief Camps closed so far (Cumulative) (II)',
  'totl_cmp_opn_tdy': 'Total number of Flood Relief Camps Operational today (I-II)',
  'totl_ppl_rgtd_til_ystd': 'Total number of people registered in Disaster relief centers till Yesterday (A)',
  'new_ppl_rgtd_tdy': 'Number of newly registered people Today (B)',
  'totl_ppl_rgtd': 'Total number of people registered so far (A+B)',
  'totl_ppl_gne_bck_fr_cmp': 'Total number of people who have gone back from the camps so far (C)',
  'ppl_rmng_in_cmp': 'Number of people remaining in the camps (A+B-C)',

  // Community Kitchens (7 columns)
  'totl_cmty_ktch_open': 'Total Number of Community Kitchens opened so far (Cumulative) (A) (Excluding meal served in flood relief camps)',
  'totl_cmty_ktch_close': 'Total number of community kitchens closed so far (Cumulative) (B)',
  'totl_cmty_ktch_open_tdy': 'Total number of Community Kitchens Operational today (A-B)',
  'num_of_ppl_etng_lun_tdy': 'Number of people eating lunch in community kitchen today',
  'num_of_ppl_etng_dinr_tngt': 'Number of people having dinner in community kitchen tonight',
  'totl_num_of_ppl_etng_tdy': 'Total number of people having meals today',
  'totl_num_of_mls_prvd_til_dt': 'Total number of meals provided till date (Cumulative)',

  // Health Centers (4 columns)
  'num_of_hlt_ctr_rng_dly': 'Number of health centers being run daily',
  'num_of_hlt_ctr_rng_til_now': 'Number of health centers being run till now (cumulative)',
  'num_of_ppl_trtd_dly': 'Number of people treated daily',
  'num_of_ppl_trtd_til_now': 'Number of people treated till now (cumulative)',

  // Sanitation (4 columns)
  'num_of_hlgn_tlbt_dist_dly': 'Number of Halogen tablets distributed daily',
  'num_of_hlgn_tlbt_dist_til_now': 'Number of Halogen tablets distributed till now (cumulative)',
  'amt_of_blhng_pdr_sprk_dly': 'Amount of bleaching powder sprinkled (KG) daily',
  'amt_of_blhng_pdr_sprk_til_now': 'Amount of bleaching powder sprinkled (KG) till now (cumulative)',

  // Animal Care (4 columns)
  'number_of_animal_camps_daily': 'Number of animal camps being run daily',
  'num_of_anml_cmp_til_now': 'Number of animal camps being run till now (cumulative)',
  'num_of_anml_trtd_dly': 'Number of animals treated daily',
  'num_of_anml_trtd_til_now': 'Number of animals treated till now (cumulative)',

  // Relief Supplies (10 columns)
  'amt_of_gr_rcmdt_tdy': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family on reporting date',
  'amt_of_gr_rcmdt_til_now': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family till now (cumulative)',
  'num_of_dry_rtn_pkt_per_fam_tdy': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) on reporting date',
  'num_of_dry_rtn_pkt_per_fam_til_now': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) till now (cumulative)',
  'num_of_fd_pkt_tdy': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. on reporting date',
  'num_of_fd_pkt_til_now': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. till now (cumulative)',
  'num_of_poly_shts_dist_tdy': 'Number of Polythene sheets distributed on reporting date',
  'num_of_poly_shts_dist_til_now': 'Number of Polythene sheets distributed till now (cumulative)',
  'otr_ingts_tdy': 'Other Ingredients (KG/Ltr/number) on reporting date',
  'otr_ingts_til_now': 'Other Ingredients (KG/Ltr/number) till now (cumulative)',
};

// Column order for Excel (124 columns total)
const EXCEL_COLUMN_ORDER = Object.values(FIELD_MAPPING);

// ============================================================================
// TRANSFORM FUNCTION - District Datewise Report
// ============================================================================

/**
 * Transform MIS District Datewise Flood Report - Following Heatwave Pattern
 * Merges daily (mis_data) + cumulative (cumulative_data) records
 */
export const transformMISDistrictDatewiseData = (rawData) => {
  console.log('🌊 transformMISDistrictDatewiseData called');

  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid flood district datewise data');
    return { columns: [], transformedData: [] };
  }

  // Extract daily and cumulative arrays
  let dailyRecords, cumRecords;

  if (rawData.mis_data && rawData.cumulative_data) {
    dailyRecords = rawData.mis_data.mis_flood_report_district_date || [];
    cumRecords = rawData.cumulative_data.mis_flood_report_district_date || [];
  } else {
    console.error('❌ Missing mis_data or cumulative_data structure');
    return { columns: [], transformedData: [] };
  }

  console.log(`- Daily records: ${dailyRecords.length}`);
  console.log(`- Cumulative records: ${cumRecords.length}`);

  if (!dailyRecords || dailyRecords.length === 0) {
    console.error('❌ No daily flood records found');
    return { columns: [], transformedData: [] };
  }

  // CREATE CUMULATIVE LOOKUP MAP (by id + date)
  const cumMap = {};
  if (cumRecords && cumRecords.length > 0) {
    cumRecords.forEach(cumRec => {
      const key = `${cumRec.id}_${cumRec.date}`;
      cumMap[key] = cumRec;
    });
  }
  console.log(`✅ Built cumulative map with ${Object.keys(cumMap).length} entries`);

  // Create columns
  const columns = EXCEL_COLUMN_ORDER.map(label => ({ label, key: label }));

  // MERGE daily + cumulative data for each row
  const transformedData = dailyRecords.map((dailyRec) => {
    const transformedRow = {};

    // Get matching cumulative record
    const cumKey = `${dailyRec.id}_${dailyRec.date}`;
    const cumRec = cumMap[cumKey] || {};

    // Process each field in mapping
    Object.keys(FIELD_MAPPING).forEach(apiField => {
      const displayName = FIELD_MAPPING[apiField];

      if (apiField === 'date') {
        // Date formatting (DD-MM-YYYY)
        transformedRow[displayName] = formatDateForMISExcel(dailyRec[apiField]);
      } else if (apiField.endsWith('_ystd') || apiField.endsWith('_til_ystd')) {
        // Yesterday fields from cumulative data - show '-' if null/undefined
        let value = cumRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';
      } else if (apiField.endsWith('_till_now') || apiField.endsWith('_til_now') ||
                 ['totl_pop_aftd', 'totl_fam_aftd', 'totl_anml_aftd', 'aftd_pop_rural',
                  'aftd_pop_urban', 'aftd_fam_rural', 'aftd_fam_urban', 'aftd_anml_rural',
                  'aftd_anml_urban', 'ktha_cmplt', 'ktha_prtl', 'crct_cmplt', 'crct_prtl',
                  'ctg_hut', 'anml_shd', 'estd_hse_val', 'estd_pub_prty_val'].includes(apiField)) {
        // Cumulative fields from cumulative data - show '-' if null/undefined
        let value = cumRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';
      } else if (apiField === 'river_name') {
        // River name
        transformedRow[displayName] = dailyRec[apiField] || 'NA';
      } else {
        // Today/Current fields from daily data
        let value = dailyRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : 0;
      }
    });

    return transformedRow;
  });

  // Sort by date (ascending - use DD-MM-YYYY string comparison)
  transformedData.sort((a, b) => {
    const dateA = a['Date'] || '';
    const dateB = b['Date'] || '';
    // Convert DD-MM-YYYY to YYYY-MM-DD for proper sorting
    const convertToSortable = (ddmmyyyy) => {
      if (!ddmmyyyy || ddmmyyyy === 'NA') return '';
      const [d, m, y] = ddmmyyyy.split('-');
      return `${y}-${m}-${d}`;
    };
    return convertToSortable(dateA).localeCompare(convertToSortable(dateB));
  });

  // Add TOTAL row
  console.log('🔢 Calculating TOTAL row...');
  const totalRow = {
    'Date': 'NA',
    'Block Name': 'NA'
  };

  // Sum ALL numeric columns (no special cases)
  EXCEL_COLUMN_ORDER.forEach(columnLabel => {
    if (columnLabel === 'Date' || columnLabel === 'Block Name') {
      totalRow[columnLabel] = 'NA';
      return;
    }

    if (columnLabel.includes('Name of major river')) {
      totalRow[columnLabel] = 'NA';
      return;
    }

    // SUM LOGIC: Handles string numbers + skips non-numeric
    let sum = 0;
    let hasValidData = false;

    transformedData.forEach(row => {
      const value = row[columnLabel];

      // Convert '20' → 20, skip '-'/'NA'
      const numValue = (typeof value === 'string' && value !== '-' && value !== 'NA' && !isNaN(parseFloat(value)))
        ? parseFloat(value)
        : (typeof value === 'number' ? value : null);

      if (numValue !== null && !isNaN(numValue)) {
        sum += numValue;
        hasValidData = true;
      }
    });

    totalRow[columnLabel] = hasValidData ? sum.toString() : 'NA';
  });

  transformedData.push(totalRow);
  console.log('✅ TOTAL ROW (Meals Col 103):', totalRow['Total number of meals provided till date (Cumulative)']);

  return { columns, transformedData };
};

/**
 * Transform MIS District Flood Report (NO date column)
 * Data: rawData.data (single array, no date grouping)
 */
export const transformMISDistrictData = (rawData) => {
  console.log('🌊 transformMISDistrictData called');

  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid flood district data');
    return { columns: [], transformedData: [] };
  }

  // Extract data array directly (no mis_data wrapper)
  let records = rawData.data || rawData;

  if (!Array.isArray(records)) {
    console.error('❌ Data is not an array');
    return { columns: [], transformedData: [] };
  }

  console.log(`- District records: ${records.length}`);

  if (records.length === 0) {
    console.error('❌ No district flood records found');
    return { columns: [], transformedData: [] };
  }

  // FIELD MAPPING for District Report (NO date field, use block_name as District Name)
  const DISTRICT_FIELD_MAPPING = {
    'block_name': 'Block Name',
    // Infrastructure (Cumulative) - 7 columns
    'totl_grm_pnchyt': 'Rural area: Total number of Gram Panchayats affected so far (Cumulative)',
    'nos_vilge_aftd': 'Rural area: Number of villages affected so far (Cumulative)',
    'totl_ngr_pnchyt': 'Urban Area: Total number of Nagar Panchayats affected so far (Cumulative)',
    'totl_mucpl_corp': 'Urban Area: Total affected Municipal corporation (Cumulative)',
    'totl_ngr_parsad': 'Urban Area: Total number of affected Nagar Parishad (Cumulative)',
    'nos_wrd_aftd': 'Urban Area: Number of wards affected so far (Cumulative)',
    'totl_aftd': 'Urban Area: Total',

    // Affected Population - Today (3 columns)
    'aftd_pop_rural_today': 'Affected population in Rural area Today (Newly affected)',
    'aftd_pop_urban_today': 'Affected population in Urban area Today (Newly affected)',
    'aftd_pop_totl_today': 'Affected population in Total Today (Newly affected)',

    // Affected Population - Until Yesterday (3 columns)
    'aftd_pop_rural_ystd': 'Affected population in Rural area Until Yesterday',
    'aftd_pop_urban_ystd': 'Affected population in Urban area Until Yesterday',
    'aftd_pop_totl_ystd': 'Affected population in Total Until Yesterday',

    // Affected Population - Cumulative (3 columns)
    'aftd_pop_rural': 'Affected population in Rural area so far (Cumulative)',
    'aftd_pop_urban': 'Affected population in Urban area so far (Cumulative)',
    'totl_pop_aftd': 'Affected population in Total so far (Cumulative)',

    // Families - Today (3 columns)
    'aftd_fam_rural_today': 'Number of families affected in Rural area Today (Newly affected)',
    'aftd_fam_urban_today': 'Number of families affected in Urban area Today (Newly affected)',
    'aftd_fam_totl_today': 'Number of families affected in Total Today (Newly affected)',

    // Families - Until Yesterday (3 columns)
    'aftd_fam_rural_ystd': 'Number of families affected in Rural area Until Yesterday',
    'aftd_fam_urban_ystd': 'Number of families affected in Urban area Until Yesterday',
    'aftd_fam_totl_ystd': 'Number of families affected in Total Until Yesterday',

    // Families - Cumulative (3 columns)
    'aftd_fam_rural': 'Number of families affected in Rural area so far (Cumulative)',
    'aftd_fam_urban': 'Number of families affected in Urban area so far (Cumulative)',
    'totl_fam_aftd': 'Number of families affected in Total so far (Cumulative)',

    // Animals - Today (3 columns)
    'aftd_anml_rural_today': 'Number of animals affected in Rural Today (Newly affected)',
    'aftd_anml_urban_today': 'Number of animals affected in Urban Today (Newly affected)',
    'aftd_anml_totl_today': 'Number of animals affected in Total Today (Newly affected)',

    // Animals - Until Yesterday (3 columns)
    'aftd_anml_rural_ystd': 'Number of animals affected in Rural area Until Yesterday',
    'aftd_anml_urban_ystd': 'Number of animals affected in Urban area Until Yesterday',
    'aftd_anml_totl_ystd': 'Number of animals affected in Total Until Yesterday',

    // Animals - Cumulative (3 columns)
    'aftd_anml_rural': 'Number of animals affected in Rural area so far (Cumulative)',
    'aftd_anml_urban': 'Number of animals affected in Urban area so far (Cumulative)',
    'totl_anml_aftd': 'Number of animals affected in Total so far (Cumulative)',

    // Geographic/Current Status (7 columns)
    'river_name': 'Name of major river which caused flood situation',
    'affected_blocks_count': 'How many blocks are currently affected by floods?',
    'affected_gram_panchayats': 'How many gram panchayats are currently affected by floods?',
    'affected_municipal_bodies': 'Number of municipal bodies (Nagar Panchayat + Municipal Council + Municipal Corporation) currently affected',
    'affected_villages': 'How many villages are currently affected by floods?',
    'affected_wards': 'How many wards are currently affected by floods?',
    'affected_population': 'How many population are currently affected by floods? (Rural area + Urban area)',

    // Area Affected (4 columns)
    'agricultural_area_affected_curr': 'Agricultural area affected in hectares on reporting date',
    'agricultural_area_affected_till_now': 'Agricultural area affected in hectares till now (cumulative)',
    'non_agricultural_area_affected_curr': 'Non-agricultural area affected in hectares on reporting date',
    'non_agricultural_area_affected_till_now': 'Non-agricultural area affected in hectares till now (cumulative)',

    // Crop Damage (4 columns)
    'damaged_crop_area_affected_curr': 'Damaged crop area in hectares on reporting date',
    'damaged_crop_area_affected_till_now': 'Damaged crop area in hectares till now (cumulative)',
    'estimated_value_of_damaged_crops_affected_curr': 'Estimated value of damaged crops in Rs Lakhs on reporting date',
    'estimated_value_of_damaged_crops_affected_till_now': 'Estimated value of damaged crops in Rs Lakhs till now (cumulative)',

    // Houses - Kutcha (4 columns)
    'ktha_cmplt_today': 'Number of Kutcha houses completely damaged on reporting date',
    'ktha_cmplt': 'Number of Kutcha houses completely damaged till now (cumulative)',
    'ktha_prtl_today': 'Number of Kutcha houses partially damaged on reporting date',
    'ktha_prtl': 'Number of Kutcha houses partially damaged till now (cumulative)',

    // Houses - Concrete (4 columns)
    'crct_cmplt_today': 'Number of Concrete houses completely damaged on reporting date',
    'crct_cmplt': 'Number of Concrete houses completely damaged till now (cumulative)',
    'crct_prtl_today': 'Number of Concrete houses partially damaged on reporting date',
    'crct_prtl': 'Number of Concrete houses partially damaged till now (cumulative)',

    // Houses - Cottage/Huts (2 columns)
    'ctg_hut_today': 'Number of Cottage/Huts damaged on reporting date',
    'ctg_hut': 'Number of Cottage/Huts damaged till now (cumulative)',

    // Houses - Animal Sheds (2 columns)
    'anml_shd_today': 'Number of Animal sheds damaged on reporting date',
    'anml_shd': 'Number of Animal sheds damaged till now (cumulative)',

    // House Values (4 columns)
    'estd_hse_val_today': 'Estimated value of damaged Houses in Rs lakh on the reporting date',
    'estd_hse_val': 'Estimated value of damaged Houses in Rs lakh till now (Cumulative)',
    'estd_pub_prty_val_today': 'Estimated value of damaged Public property in Rs lakh on the reporting date',
    'estd_pub_prty_val': 'Estimated value of damaged Public property in Rs lakh till now (Cumulative)',

    // Boats (4 columns)
    'damaged_boats_complete_curr': 'Number of completely damaged boats on reporting date',
    'damaged_boats_complete_till_now': 'Number of completely damaged boats till now (cumulative)',
    'damaged_boats_partial_curr': 'Number of partially damaged boats on reporting date',
    'damaged_boats_partial_till_now': 'Number of partially damaged boats till now (cumulative)',

    // Nets (4 columns)
    'damaged_net_complete_curr': 'Number of completely damaged nets on reporting date',
    'damaged_net_complete_till_now': 'Number of completely damaged nets till now (cumulative)',
    'damaged_net_partial_curr': 'Number of partially damaged nets on reporting date',
    'damaged_net_partial_till_now': 'Number of partially damaged nets till now (cumulative)',

    // Fish Farms (2 columns)
    'area_of_damaged_fish_egg_farm_curr': 'Area of damaged fish egg farm in hectares on the reporting date',
    'area_of_damaged_fish_egg_farm_till_now': 'Area of damaged fish egg farm in hectares till now (Cumulative)',

    // Deaths (4 columns)
    'human_deaths_curr': 'Human death count on reporting date',
    'human_deaths_till_now': 'Human death count till now (cumulative)',
    'animal_deaths_curr': 'Animal death count on reporting date',
    'animal_deaths_till_now': 'Animal death count till now (cumulative)',

    // Motorboats (3 columns)
    'mtr_bot': 'Number of motorboats operated/used',
    'govt_bot': 'Number of Government boats operated/used',
    'pvt_bot': 'Number of Private boats operated/used',

    // Evacuation (3 columns)
    'totl_evctd_till_ystd': 'Total number of Population evacuated till yesterday (A)',
    'totl_evctd_today': 'Total number of Population evacuated today (B)',
    'totl_evctd_till_now': 'Total number of Population evacuated till now (A+B)',

    // Relief Camps (8 columns)
    'totl_cmp_opn': 'Total number of Flood Relief Camps opened so far (Cumulative) (I)',
    'totl_cmp_cls': 'Total number of Flood Relief Camps closed so far (Cumulative) (II)',
    'totl_cmp_opn_tdy': 'Total number of Flood Relief Camps Operational today (I-II)',
    'totl_ppl_rgtd_til_ystd': 'Total number of people registered in Disaster relief centers till Yesterday (A)',
    'new_ppl_rgtd_tdy': 'Number of newly registered people Today (B)',
    'totl_ppl_rgtd': 'Total number of people registered so far (A+B)',
    'totl_ppl_gne_bck_fr_cmp': 'Total number of people who have gone back from the camps so far (C)',
    'ppl_rmng_in_cmp': 'Number of people remaining in the camps (A+B-C)',

    // Community Kitchens (7 columns)
    'totl_cmty_ktch_open': 'Total Number of Community Kitchens opened so far (Cumulative) (A) (Excluding meal served in flood relief camps)',
    'totl_cmty_ktch_close': 'Total number of community kitchens closed so far (Cumulative) (B)',
    'totl_cmty_ktch_open_tdy': 'Total number of Community Kitchens Operational today (A-B)',
    'num_of_ppl_etng_lun_tdy': 'Number of people eating lunch in community kitchen today',
    'num_of_ppl_etng_dinr_tngt': 'Number of people having dinner in community kitchen tonight',
    'totl_num_of_ppl_etng_tdy': 'Total number of people having meals today',
    'totl_num_of_mls_prvd_til_dt': 'Total number of meals provided till date (Cumulative)',

    // Health Centers (4 columns)
    'num_of_hlt_ctr_rng_dly': 'Number of health centers being run daily',
    'num_of_hlt_ctr_rng_til_now': 'Number of health centers being run till now (cumulative)',
    'num_of_ppl_trtd_dly': 'Number of people treated daily',
    'num_of_ppl_trtd_til_now': 'Number of people treated till now (cumulative)',

    // Sanitation (4 columns)
    'num_of_hlgn_tlbt_dist_dly': 'Number of Halogen tablets distributed daily',
    'num_of_hlgn_tlbt_dist_til_now': 'Number of Halogen tablets distributed till now (cumulative)',
    'amt_of_blhng_pdr_sprk_dly': 'Amount of bleaching powder sprinkled (KG) daily',
    'amt_of_blhng_pdr_sprk_til_now': 'Amount of bleaching powder sprinkled (KG) till now (cumulative)',

    // Animal Care (4 columns)
    'number_of_animal_camps_daily': 'Number of animal camps being run daily',
    'num_of_anml_cmp_til_now': 'Number of animal camps being run till now (cumulative)',
    'num_of_anml_trtd_dly': 'Number of animals treated daily',
    'num_of_anml_trtd_til_now': 'Number of animals treated till now (cumulative)',

    // Relief Supplies (10 columns)
    'amt_of_gr_rcmdt_tdy': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family on reporting date',
    'amt_of_gr_rcmdt_til_now': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family till now (cumulative)',
    'num_of_dry_rtn_pkt_per_fam_tdy': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) on reporting date',
    'num_of_dry_rtn_pkt_per_fam_til_now': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) till now (cumulative)',
    'num_of_fd_pkt_tdy': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. on reporting date',
    'num_of_fd_pkt_til_now': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. till now (cumulative)',
    'num_of_poly_shts_dist_tdy': 'Number of Polythene sheets distributed on reporting date',
    'num_of_poly_shts_dist_til_now': 'Number of Polythene sheets distributed till now (cumulative)',
    'otr_ingts_tdy': 'Other Ingredients (KG/Ltr/number) on reporting date',
    'otr_ingts_til_now': 'Other Ingredients (KG/Ltr/number) till now (cumulative)',
  };

  const DISTRICT_COLUMN_ORDER = Object.values(DISTRICT_FIELD_MAPPING);

  // Create columns
  const columns = DISTRICT_COLUMN_ORDER.map(label => ({ label, key: label }));

  // Transform each record
  const transformedData = records.map((rec) => {
    const transformedRow = {};

    Object.keys(DISTRICT_FIELD_MAPPING).forEach(apiField => {
      const displayName = DISTRICT_FIELD_MAPPING[apiField];

      if (apiField === 'block_name') {
        // District Name field
        transformedRow[displayName] = rec[apiField] || 'NA';
      } else if (apiField === 'river_name') {
        // River name
        transformedRow[displayName] = rec[apiField] || 'NA';
      } else {
        // All other fields
        let value = rec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';
      }
    });

    return transformedRow;
  });

  // Add TOTAL row
  console.log('🔢 Calculating TOTAL row...');
  const totalRow = {
    'District Name': 'NA'
  };

  DISTRICT_COLUMN_ORDER.forEach(columnLabel => {
    if (columnLabel === 'District Name') {
      totalRow[columnLabel] = 'NA';
      return;
    }

    if (columnLabel.includes('Name of major river')) {
      totalRow[columnLabel] = 'NA';
      return;
    }

    // Sum all numeric values
    let sum = 0;
    let hasValidData = false;

    transformedData.forEach(row => {
      const value = row[columnLabel];

      const numValue = (typeof value === 'string' && value !== '-' && value !== 'NA' && !isNaN(parseFloat(value)))
        ? parseFloat(value)
        : (typeof value === 'number' ? value : null);

      if (numValue !== null && !isNaN(numValue)) {
        sum += numValue;
        hasValidData = true;
      }
    });

    totalRow[columnLabel] = hasValidData ? sum.toString() : 'NA';
  });

  transformedData.push(totalRow);
  console.log('✅ TOTAL ROW (Meals):', totalRow['Total number of meals provided till date (Cumulative)']);

  console.log(`✅ Transformed ${transformedData.length} district rows`);
  return { columns, transformedData };
};

/**
 * Transform MIS State Datewise Flood Report - Following Same Pattern as District Datewise
 * Merges daily (mis_data) + cumulative (cumulative_data) records
 */
export const transformMISStateDatewiseData = (rawData) => {
  console.log('🌊 transformMISStateDatewiseData called');

  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid flood state datewise data');
    return { columns: [], transformedData: [] };
  }

  // Extract daily and cumulative arrays
  let dailyRecords, cumRecords;

  if (rawData.mis_data && rawData.cumulative_data) {
    dailyRecords = rawData.mis_data.mis_flood_report_state_date || [];
    cumRecords = rawData.cumulative_data.mis_flood_report_state_date || [];
  } else {
    console.error('❌ Missing mis_data or cumulative_data structure');
    return { columns: [], transformedData: [] };
  }

  console.log(`- Daily records: ${dailyRecords.length}`);
  console.log(`- Cumulative records: ${cumRecords.length}`);

  if (!dailyRecords || dailyRecords.length === 0) {
    console.error('❌ No daily flood state records found');
    return { columns: [], transformedData: [] };
  }

  // ✅ CREATE CUMULATIVE LOOKUP MAP (by id + date)
  const cumMap = {};
  if (cumRecords && cumRecords.length > 0) {
    cumRecords.forEach(cumRec => {
      const key = `${cumRec.id}_${cumRec.date}`;
      cumMap[key] = cumRec;
    });
  }

  console.log(`✅ Built cumulative map with ${Object.keys(cumMap).length} entries`);

  // ✅ SAME FIELD MAPPING AS DISTRICT DATEWISE (124 columns)
  const FIELD_MAPPING = {
    // Row identification - NOTE: "name" is district name for state report
    'date': 'Date',
    'name': 'District Name',

    // Infrastructure (Cumulative) - 7 columns
    'totl_grm_pnchyt': 'Rural area: Total number of Gram Panchayats affected so far (Cumulative)',
    'nos_vilge_aftd': 'Rural area: Number of villages affected so far (Cumulative)',
    'totl_ngr_pnchyt': 'Urban Area: Total number of Nagar Panchayats affected so far (Cumulative)',
    'totl_mucpl_corp': 'Urban Area: Total affected Municipal corporation (Cumulative)',
    'totl_ngr_parsad': 'Urban Area: Total number of affected Nagar Parishad (Cumulative)',
    'nos_wrd_aftd': 'Urban Area: Number of wards affected so far (Cumulative)',
    'totl_aftd': 'Urban Area: Total',

    // Affected Population - Today (3 columns)
    'aftd_pop_rural_today': 'Affected population in Rural area Today (Newly affected)',
    'aftd_pop_urban_today': 'Affected population in Urban area Today (Newly affected)',
    'aftd_pop_totl_today': 'Affected population in Total Today (Newly affected)',

    // Affected Population - Until Yesterday (3 columns)
    'aftd_pop_rural_ystd': 'Affected population in Rural area Until Yesterday',
    'aftd_pop_urban_ystd': 'Affected population in Urban area Until Yesterday',
    'aftd_pop_totl_ystd': 'Affected population in Total Until Yesterday',

    // Affected Population - Cumulative (3 columns)
    'aftd_pop_rural': 'Affected population in Rural area so far (Cumulative)',
    'aftd_pop_urban': 'Affected population in Urban area so far (Cumulative)',
    'totl_pop_aftd': 'Affected population in Total so far (Cumulative)',

    // Families - Today (3 columns)
    'aftd_fam_rural_today': 'Number of families affected in Rural area Today (Newly affected)',
    'aftd_fam_urban_today': 'Number of families affected in Urban area Today (Newly affected)',
    'aftd_fam_totl_today': 'Number of families affected in Total Today (Newly affected)',

    // Families - Until Yesterday (3 columns)
    'aftd_fam_rural_ystd': 'Number of families affected in Rural area Until Yesterday',
    'aftd_fam_urban_ystd': 'Number of families affected in Urban area Until Yesterday',
    'aftd_fam_totl_ystd': 'Number of families affected in Total Until Yesterday',

    // Families - Cumulative (3 columns)
    'aftd_fam_rural': 'Number of families affected in Rural area so far (Cumulative)',
    'aftd_fam_urban': 'Number of families affected in Urban area so far (Cumulative)',
    'totl_fam_aftd': 'Number of families affected in Total so far (Cumulative)',

    // Animals - Today (3 columns)
    'aftd_anml_rural_today': 'Number of animals affected in Rural Today (Newly affected)',
    'aftd_anml_urban_today': 'Number of animals affected in Urban Today (Newly affected)',
    'aftd_anml_totl_today': 'Number of animals affected in Total Today (Newly affected)',

    // Animals - Until Yesterday (3 columns)
    'aftd_anml_rural_ystd': 'Number of animals affected in Rural area Until Yesterday',
    'aftd_anml_urban_ystd': 'Number of animals affected in Urban area Until Yesterday',
    'aftd_anml_totl_ystd': 'Number of animals affected in Total Until Yesterday',

    // Animals - Cumulative (3 columns)
    'aftd_anml_rural': 'Number of animals affected in Rural area so far (Cumulative)',
    'aftd_anml_urban': 'Number of animals affected in Urban area so far (Cumulative)',
    'totl_anml_aftd': 'Number of animals affected in Total so far (Cumulative)',

    // Geographic/Current Status (7 columns)
    'river_name': 'Name of major river which caused flood situation',
    'affected_blocks_count': 'How many blocks are currently affected by floods?',
    'affected_gram_panchayats': 'How many gram panchayats are currently affected by floods?',
    'affected_municipal_bodies': 'Number of municipal bodies (Nagar Panchayat + Municipal Council + Municipal Corporation) currently affected',
    'affected_villages': 'How many villages are currently affected by floods?',
    'affected_wards': 'How many wards are currently affected by floods?',
    'affected_population': 'How many population are currently affected by floods? (Rural area + Urban area)',

    // Area Affected (4 columns)
    'agricultural_area_affected_curr': 'Agricultural area affected in hectares on reporting date',
    'agricultural_area_affected_till_now': 'Agricultural area affected in hectares till now (cumulative)',
    'non_agricultural_area_affected_curr': 'Non-agricultural area affected in hectares on reporting date',
    'non_agricultural_area_affected_till_now': 'Non-agricultural area affected in hectares till now (cumulative)',

    // Crop Damage (4 columns)
    'damaged_crop_area_affected_curr': 'Damaged crop area in hectares on reporting date',
    'damaged_crop_area_affected_till_now': 'Damaged crop area in hectares till now (cumulative)',
    'estimated_value_of_damaged_crops_affected_curr': 'Estimated value of damaged crops in Rs Lakhs on reporting date',
    'estimated_value_of_damaged_crops_affected_till_now': 'Estimated value of damaged crops in Rs Lakhs till now (cumulative)',

    // Houses - Kutcha (4 columns)
    'ktha_cmplt_today': 'Number of Kutcha houses completely damaged on reporting date',
    'ktha_cmplt': 'Number of Kutcha houses completely damaged till now (cumulative)',
    'ktha_prtl_today': 'Number of Kutcha houses partially damaged on reporting date',
    'ktha_prtl': 'Number of Kutcha houses partially damaged till now (cumulative)',

    // Houses - Concrete (4 columns)
    'crct_cmplt_today': 'Number of Concrete houses completely damaged on reporting date',
    'crct_cmplt': 'Number of Concrete houses completely damaged till now (cumulative)',
    'crct_prtl_today': 'Number of Concrete houses partially damaged on reporting date',
    'crct_prtl': 'Number of Concrete houses partially damaged till now (cumulative)',

    // Houses - Cottage/Huts (2 columns)
    'ctg_hut_today': 'Number of Cottage/Huts damaged on reporting date',
    'ctg_hut': 'Number of Cottage/Huts damaged till now (cumulative)',

    // Houses - Animal Sheds (2 columns)
    'anml_shd_today': 'Number of Animal sheds damaged on reporting date',
    'anml_shd': 'Number of Animal sheds damaged till now (cumulative)',

    // House Values (4 columns)
    'estd_hse_val_today': 'Estimated value of damaged Houses in Rs lakh on the reporting date',
    'estd_hse_val': 'Estimated value of damaged Houses in Rs lakh till now (Cumulative)',
    'estd_pub_prty_val_today': 'Estimated value of damaged Public property in Rs lakh on the reporting date',
    'estd_pub_prty_val': 'Estimated value of damaged Public property in Rs lakh till now (Cumulative)',

    // Boats (4 columns)
    'damaged_boats_complete_curr': 'Number of completely damaged boats on reporting date',
    'damaged_boats_complete_till_now': 'Number of completely damaged boats till now (cumulative)',
    'damaged_boats_partial_curr': 'Number of partially damaged boats on reporting date',
    'damaged_boats_partial_till_now': 'Number of partially damaged boats till now (cumulative)',

    // Nets (4 columns)
    'damaged_net_complete_curr': 'Number of completely damaged nets on reporting date',
    'damaged_net_complete_till_now': 'Number of completely damaged nets till now (cumulative)',
    'damaged_net_partial_curr': 'Number of partially damaged nets on reporting date',
    'damaged_net_partial_till_now': 'Number of partially damaged nets till now (cumulative)',

    // Fish Farms (2 columns)
    'area_of_damaged_fish_egg_farm_curr': 'Area of damaged fish egg farm in hectares on the reporting date',
    'area_of_damaged_fish_egg_farm_till_now': 'Area of damaged fish egg farm in hectares till now (Cumulative)',

    // Deaths (4 columns)
    'human_deaths_curr': 'Human death count on reporting date',
    'human_deaths_till_now': 'Human death count till now (cumulative)',
    'animal_deaths_curr': 'Animal death count on reporting date',
    'animal_deaths_till_now': 'Animal death count till now (cumulative)',

    // Motorboats (3 columns)
    'mtr_bot': 'Number of motorboats operated/used',
    'govt_bot': 'Number of Government boats operated/used',
    'pvt_bot': 'Number of Private boats operated/used',

    // Evacuation (3 columns)
    'totl_evctd_till_ystd': 'Total number of Population evacuated till yesterday (A)',
    'totl_evctd_today': 'Total number of Population evacuated today (B)',
    'totl_evctd_till_now': 'Total number of Population evacuated till now (A+B)',

    // Relief Camps (8 columns)
    'totl_cmp_opn': 'Total number of Flood Relief Camps opened so far (Cumulative) (I)',
    'totl_cmp_cls': 'Total number of Flood Relief Camps closed so far (Cumulative) (II)',
    'totl_cmp_opn_tdy': 'Total number of Flood Relief Camps Operational today (I-II)',
    'totl_ppl_rgtd_til_ystd': 'Total number of people registered in Disaster relief centers till Yesterday (A)',
    'new_ppl_rgtd_tdy': 'Number of newly registered people Today (B)',
    'totl_ppl_rgtd': 'Total number of people registered so far (A+B)',
    'totl_ppl_gne_bck_fr_cmp': 'Total number of people who have gone back from the camps so far (C)',
    'ppl_rmng_in_cmp': 'Number of people remaining in the camps (A+B-C)',

    // Community Kitchens (7 columns)
    'totl_cmty_ktch_open': 'Total Number of Community Kitchens opened so far (Cumulative) (A) (Excluding meal served in flood relief camps)',
    'totl_cmty_ktch_close': 'Total number of community kitchens closed so far (Cumulative) (B)',
    'totl_cmty_ktch_open_tdy': 'Total number of Community Kitchens Operational today (A-B)',
    'num_of_ppl_etng_lun_tdy': 'Number of people eating lunch in community kitchen today',
    'num_of_ppl_etng_dinr_tngt': 'Number of people having dinner in community kitchen tonight',
    'totl_num_of_ppl_etng_tdy': 'Total number of people having meals today',
    'totl_num_of_mls_prvd_til_dt': 'Total number of meals provided till date (Cumulative)',

    // Health Centers (4 columns)
    'num_of_hlt_ctr_rng_dly': 'Number of health centers being run daily',
    'num_of_hlt_ctr_rng_til_now': 'Number of health centers being run till now (cumulative)',
    'num_of_ppl_trtd_dly': 'Number of people treated daily',
    'num_of_ppl_trtd_til_now': 'Number of people treated till now (cumulative)',

    // Sanitation (4 columns)
    'num_of_hlgn_tlbt_dist_dly': 'Number of Halogen tablets distributed daily',
    'num_of_hlgn_tlbt_dist_til_now': 'Number of Halogen tablets distributed till now (cumulative)',
    'amt_of_blhng_pdr_sprk_dly': 'Amount of bleaching powder sprinkled (KG) daily',
    'amt_of_blhng_pdr_sprk_til_now': 'Amount of bleaching powder sprinkled (KG) till now (cumulative)',

    // Animal Care (4 columns)
    'number_of_animal_camps_daily': 'Number of animal camps being run daily',
    'num_of_anml_cmp_til_now': 'Number of animal camps being run till now (cumulative)',
    'num_of_anml_trtd_dly': 'Number of animals treated daily',
    'num_of_anml_trtd_til_now': 'Number of animals treated till now (cumulative)',

    // Relief Supplies (10 columns)
    'amt_of_gr_rcmdt_tdy': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family on reporting date',
    'amt_of_gr_rcmdt_til_now': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family till now (cumulative)',
    'num_of_dry_rtn_pkt_per_fam_tdy': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) on reporting date',
    'num_of_dry_rtn_pkt_per_fam_til_now': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) till now (cumulative)',
    'num_of_fd_pkt_tdy': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. on reporting date',
    'num_of_fd_pkt_til_now': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. till now (cumulative)',
    'num_of_poly_shts_dist_tdy': 'Number of Polythene sheets distributed on reporting date',
    'num_of_poly_shts_dist_til_now': 'Number of Polythene sheets distributed till now (cumulative)',
    'otr_ingts_tdy': 'Other Ingredients (KG/Ltr/number) on reporting date',
    'otr_ingts_til_now': 'Other Ingredients (KG/Ltr/number) till now (cumulative)',
  };

  const EXCEL_COLUMN_ORDER = Object.values(FIELD_MAPPING);
  const columns = EXCEL_COLUMN_ORDER.map(label => ({ label, key: label }));

  // ✅ MERGE daily + cumulative data for each row
  const transformedData = dailyRecords.map((dailyRec) => {
    const transformedRow = {};

    // Get matching cumulative record
    const cumKey = `${dailyRec.id}_${dailyRec.date}`;
    const cumRec = cumMap[cumKey] || {};

    // Process each field in mapping
    Object.keys(FIELD_MAPPING).forEach(apiField => {
      const displayName = FIELD_MAPPING[apiField];

      if (apiField === 'date') {
        // Date formatting (DD-MM-YYYY)
        transformedRow[displayName] = formatDateForMISExcel(dailyRec[apiField]);

      } else if (apiField.endsWith('_ystd') || apiField.endsWith('_til_ystd')) {
        // Yesterday fields from cumulative data - show '-' if null/undefined
        let value = cumRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';

      } else if (apiField.endsWith('_till_now') || apiField.endsWith('_til_now') ||
                 ['totl_pop_aftd', 'totl_fam_aftd', 'totl_anml_aftd', 'aftd_pop_rural',
                  'aftd_pop_urban', 'aftd_fam_rural', 'aftd_fam_urban', 'aftd_anml_rural',
                  'aftd_anml_urban', 'ktha_cmplt', 'ktha_prtl', 'crct_cmplt', 'crct_prtl',
                  'ctg_hut', 'anml_shd', 'estd_hse_val', 'estd_pub_prty_val'].includes(apiField)) {
        // Cumulative fields from cumulative data - show '-' if null/undefined
        let value = cumRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';

      } else if (apiField === 'river_name') {
        // River name
        transformedRow[displayName] = dailyRec[apiField] || 'NA';

      } else {
        // Today/Current fields from daily data
        let value = dailyRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : 0;
      }
    });

    return transformedRow;
  });

  // Sort by date (ascending - use DD-MM-YYYY string comparison)
  transformedData.sort((a, b) => {
    const dateA = a['Date'] || '';
    const dateB = b['Date'] || '';

    // Convert DD-MM-YYYY to YYYY-MM-DD for proper sorting
    const convertToSortable = (ddmmyyyy) => {
      if (!ddmmyyyy || ddmmyyyy === 'NA') return '';
      const [d, m, y] = ddmmyyyy.split('-');
      return `${y}-${m}-${d}`;
    };

    return convertToSortable(dateA).localeCompare(convertToSortable(dateB));
  });

  // Add TOTAL row
  console.log('🔢 Calculating TOTAL row...');
  const totalRow = {
    'Date': 'NA',
    'District Name': 'Total'
  };

  // Sum ALL numeric columns (no special cases)
  EXCEL_COLUMN_ORDER.forEach(columnLabel => {
    if (columnLabel === 'Date' || columnLabel === 'District Name') {
      //totalRow[columnLabel] = 'NA';
      return;
    }

    if (columnLabel.includes('Name of major river')) {
      totalRow[columnLabel] = 'NA';
      return;
    }

    // SUM LOGIC: Handles string numbers + skips non-numeric
    let sum = 0;
    let hasValidData = false;

    transformedData.forEach(row => {
      const value = row[columnLabel];

      // Convert '20' → 20, skip '-'/'NA'
      const numValue = (typeof value === 'string' && value !== '-' && value !== 'NA' && !isNaN(parseFloat(value)))
        ? parseFloat(value)
        : (typeof value === 'number' ? value : null);

      if (numValue !== null && !isNaN(numValue)) {
        sum += numValue;
        hasValidData = true;
      }
    });

    totalRow[columnLabel] = hasValidData ? sum.toString() : 'NA';
  });

  transformedData.push(totalRow);

  console.log('✅ Transformed State Datewise data', transformedData.length, 'rows');

  return { columns, transformedData };
};

/**
 * Transform MIS State Flood Report (NO date column)
 * Similar to District Report but for State-level aggregated data
 * Data: rawData.data (single array, no date grouping)
 */
export const transformMISStateData = (rawData) => {
  console.log('🌊 transformMISStateData called');

  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid flood state data');
    return { columns: [], transformedData: [] };
  }

  // Extract data array directly (no mis_data wrapper)
  let records = rawData.data || rawData;
  if (!Array.isArray(records)) {
    console.error('❌ Data is not an array');
    return { columns: [], transformedData: [] };
  }

  console.log(`- State records: ${records.length}`);
  if (records.length === 0) {
    console.error('❌ No state flood records found');
    return { columns: [], transformedData: [] };
  }

  // FIELD MAPPING for State Report (NO date field, use 'name' as District Name)
  const STATE_FIELD_MAPPING = {
    'name': 'District Name',

    // Infrastructure (Cumulative) - 7 columns
    'totl_grm_pnchyt': 'Rural area: Total number of Gram Panchayats affected so far (Cumulative)',
    'nos_vilge_aftd': 'Rural area: Number of villages affected so far (Cumulative)',
    'totl_ngr_pnchyt': 'Urban Area: Total number of Nagar Panchayats affected so far (Cumulative)',
    'totl_mucpl_corp': 'Urban Area: Total affected Municipal corporation (Cumulative)',
    'totl_ngr_parsad': 'Urban Area: Total number of affected Nagar Parishad (Cumulative)',
    'nos_wrd_aftd': 'Urban Area: Number of wards affected so far (Cumulative)',
    'totl_aftd': 'Urban Area: Total',

    // Affected Population - Today (3 columns)
    'aftd_pop_rural_today': 'Affected population in Rural area Today (Newly affected)',
    'aftd_pop_urban_today': 'Affected population in Urban area Today (Newly affected)',
    'aftd_pop_totl_today': 'Affected population in Total Today (Newly affected)',

    // Affected Population - Until Yesterday (3 columns)
    'aftd_pop_rural_ystd': 'Affected population in Rural area Until Yesterday',
    'aftd_pop_urban_ystd': 'Affected population in Urban area Until Yesterday',
    'aftd_pop_totl_ystd': 'Affected population in Total Until Yesterday',

    // Affected Population - Cumulative (3 columns)
    'aftd_pop_rural': 'Affected population in Rural area so far (Cumulative)',
    'aftd_pop_urban': 'Affected population in Urban area so far (Cumulative)',
    'totl_pop_aftd': 'Affected population in Total so far (Cumulative)',

    // Families - Today (3 columns)
    'aftd_fam_rural_today': 'Number of families affected in Rural area Today (Newly affected)',
    'aftd_fam_urban_today': 'Number of families affected in Urban area Today (Newly affected)',
    'aftd_fam_totl_today': 'Number of families affected in Total Today (Newly affected)',

    // Families - Until Yesterday (3 columns)
    'aftd_fam_rural_ystd': 'Number of families affected in Rural area Until Yesterday',
    'aftd_fam_urban_ystd': 'Number of families affected in Urban area Until Yesterday',
    'aftd_fam_totl_ystd': 'Number of families affected in Total Until Yesterday',

    // Families - Cumulative (3 columns)
    'aftd_fam_rural': 'Number of families affected in Rural area so far (Cumulative)',
    'aftd_fam_urban': 'Number of families affected in Urban area so far (Cumulative)',
    'totl_fam_aftd': 'Number of families affected in Total so far (Cumulative)',

    // Animals - Today (3 columns)
    'aftd_anml_rural_today': 'Number of animals affected in Rural Today (Newly affected)',
    'aftd_anml_urban_today': 'Number of animals affected in Urban Today (Newly affected)',
    'aftd_anml_totl_today': 'Number of animals affected in Total Today (Newly affected)',

    // Animals - Until Yesterday (3 columns)
    'aftd_anml_rural_ystd': 'Number of animals affected in Rural area Until Yesterday',
    'aftd_anml_urban_ystd': 'Number of animals affected in Urban area Until Yesterday',
    'aftd_anml_totl_ystd': 'Number of animals affected in Total Until Yesterday',

    // Animals - Cumulative (3 columns)
    'aftd_anml_rural': 'Number of animals affected in Rural area so far (Cumulative)',
    'aftd_anml_urban': 'Number of animals affected in Urban area so far (Cumulative)',
    'totl_anml_aftd': 'Number of animals affected in Total so far (Cumulative)',

    // Geographic/Current Status (7 columns)
    'river_name': 'Name of major river which caused flood situation',
    'affected_blocks_count': 'How many blocks are currently affected by floods?',
    'affected_gram_panchayats': 'How many gram panchayats are currently affected by floods?',
    'affected_municipal_bodies': 'Number of municipal bodies (Nagar Panchayat + Municipal Council + Municipal Corporation) currently affected',
    'affected_villages': 'How many villages are currently affected by floods?',
    'affected_wards': 'How many wards are currently affected by floods?',
    'affected_population': 'How many population are currently affected by floods? (Rural area + Urban area)',

    // Area Affected (4 columns)
    'agricultural_area_affected_curr': 'Agricultural area affected in hectares on reporting date',
    'agricultural_area_affected_till_now': 'Agricultural area affected in hectares till now (cumulative)',
    'non_agricultural_area_affected_curr': 'Non-agricultural area affected in hectares on reporting date',
    'non_agricultural_area_affected_till_now': 'Non-agricultural area affected in hectares till now (cumulative)',

    // Crop Damage (4 columns)
    'damaged_crop_area_affected_curr': 'Damaged crop area in hectares on reporting date',
    'damaged_crop_area_affected_till_now': 'Damaged crop area in hectares till now (cumulative)',
    'estimated_value_of_damaged_crops_affected_curr': 'Estimated value of damaged crops in Rs Lakhs on reporting date',
    'estimated_value_of_damaged_crops_affected_till_now': 'Estimated value of damaged crops in Rs Lakhs till now (cumulative)',

    // Houses - Kutcha (4 columns)
    'ktha_cmplt_today': 'Number of Kutcha houses completely damaged on reporting date',
    'ktha_cmplt': 'Number of Kutcha houses completely damaged till now (cumulative)',
    'ktha_prtl_today': 'Number of Kutcha houses partially damaged on reporting date',
    'ktha_prtl': 'Number of Kutcha houses partially damaged till now (cumulative)',

    // Houses - Concrete (4 columns)
    'crct_cmplt_today': 'Number of Concrete houses completely damaged on reporting date',
    'crct_cmplt': 'Number of Concrete houses completely damaged till now (cumulative)',
    'crct_prtl_today': 'Number of Concrete houses partially damaged on reporting date',
    'crct_prtl': 'Number of Concrete houses partially damaged till now (cumulative)',

    // Houses - Cottage/Huts (2 columns)
    'ctg_hut_today': 'Number of Cottage/Huts damaged on reporting date',
    'ctg_hut': 'Number of Cottage/Huts damaged till now (cumulative)',

    // Houses - Animal Sheds (2 columns)
    'anml_shd_today': 'Number of Animal sheds damaged on reporting date',
    'anml_shd': 'Number of Animal sheds damaged till now (cumulative)',

    // House Values (4 columns)
    'estd_hse_val_today': 'Estimated value of damaged Houses in Rs lakh on the reporting date',
    'estd_hse_val': 'Estimated value of damaged Houses in Rs lakh till now (Cumulative)',
    'estd_pub_prty_val_today': 'Estimated value of damaged Public property in Rs lakh on the reporting date',
    'estd_pub_prty_val': 'Estimated value of damaged Public property in Rs lakh till now (Cumulative)',

    // Boats (4 columns)
    'damaged_boats_complete_curr': 'Number of completely damaged boats on reporting date',
    'damaged_boats_complete_till_now': 'Number of completely damaged boats till now (cumulative)',
    'damaged_boats_partial_curr': 'Number of partially damaged boats on reporting date',
    'damaged_boats_partial_till_now': 'Number of partially damaged boats till now (cumulative)',

    // Nets (4 columns)
    'damaged_net_complete_curr': 'Number of completely damaged nets on reporting date',
    'damaged_net_complete_till_now': 'Number of completely damaged nets till now (cumulative)',
    'damaged_net_partial_curr': 'Number of partially damaged nets on reporting date',
    'damaged_net_partial_till_now': 'Number of partially damaged nets till now (cumulative)',

    // Fish Farms (2 columns)
    'area_of_damaged_fish_egg_farm_curr': 'Area of damaged fish egg farm in hectares on the reporting date',
    'area_of_damaged_fish_egg_farm_till_now': 'Area of damaged fish egg farm in hectares till now (Cumulative)',

    // Deaths (4 columns)
    'human_deaths_curr': 'Human death count on reporting date',
    'human_deaths_till_now': 'Human death count till now (cumulative)',
    'animal_deaths_curr': 'Animal death count on reporting date',
    'animal_deaths_till_now': 'Animal death count till now (cumulative)',

    // Motorboats (3 columns)
    'mtr_bot': 'Number of motorboats operated/used',
    'govt_bot': 'Number of Government boats operated/used',
    'pvt_bot': 'Number of Private boats operated/used',

    // Evacuation (3 columns)
    'totl_evctd_till_ystd': 'Total number of Population evacuated till yesterday (A)',
    'totl_evctd_today': 'Total number of Population evacuated today (B)',
    'totl_evctd_till_now': 'Total number of Population evacuated till now (A+B)',

    // Relief Camps (8 columns)
    'totl_cmp_opn': 'Total number of Flood Relief Camps opened so far (Cumulative) (I)',
    'totl_cmp_cls': 'Total number of Flood Relief Camps closed so far (Cumulative) (II)',
    'totl_cmp_opn_tdy': 'Total number of Flood Relief Camps Operational today (I-II)',
    'totl_ppl_rgtd_til_ystd': 'Total number of people registered in Disaster relief centers till Yesterday (A)',
    'new_ppl_rgtd_tdy': 'Number of newly registered people Today (B)',
    'totl_ppl_rgtd': 'Total number of people registered so far (A+B)',
    'totl_ppl_gne_bck_fr_cmp': 'Total number of people who have gone back from the camps so far (C)',
    'ppl_rmng_in_cmp': 'Number of people remaining in the camps (A+B-C)',

    // Community Kitchens (7 columns)
    'totl_cmty_ktch_open': 'Total Number of Community Kitchens opened so far (Cumulative) (A) (Excluding meal served in flood relief camps)',
    'totl_cmty_ktch_close': 'Total number of community kitchens closed so far (Cumulative) (B)',
    'totl_cmty_ktch_open_tdy': 'Total number of Community Kitchens Operational today (A-B)',
    'num_of_ppl_etng_lun_tdy': 'Number of people eating lunch in community kitchen today',
    'num_of_ppl_etng_dinr_tngt': 'Number of people having dinner in community kitchen tonight',
    'totl_num_of_ppl_etng_tdy': 'Total number of people having meals today',
    'totl_num_of_mls_prvd_til_dt': 'Total number of meals provided till date (Cumulative)',

    // Health Centers (4 columns)
    'num_of_hlt_ctr_rng_dly': 'Number of health centers being run daily',
    'num_of_hlt_ctr_rng_til_now': 'Number of health centers being run till now (cumulative)',
    'num_of_ppl_trtd_dly': 'Number of people treated daily',
    'num_of_ppl_trtd_til_now': 'Number of people treated till now (cumulative)',

    // Sanitation (4 columns)
    'num_of_hlgn_tlbt_dist_dly': 'Number of Halogen tablets distributed daily',
    'num_of_hlgn_tlbt_dist_til_now': 'Number of Halogen tablets distributed till now (cumulative)',
    'amt_of_blhng_pdr_sprk_dly': 'Amount of bleaching powder sprinkled (KG) daily',
    'amt_of_blhng_pdr_sprk_til_now': 'Amount of bleaching powder sprinkled (KG) till now (cumulative)',

    // Animal Care (4 columns)
    'number_of_animal_camps_daily': 'Number of animal camps being run daily',
    'num_of_anml_cmp_til_now': 'Number of animal camps being run till now (cumulative)',
    'num_of_anml_trtd_dly': 'Number of animals treated daily',
    'num_of_anml_trtd_til_now': 'Number of animals treated till now (cumulative)',

    // Relief Supplies (10 columns)
    'amt_of_gr_rcmdt_tdy': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family on reporting date',
    'amt_of_gr_rcmdt_til_now': 'Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family till now (cumulative)',
    'num_of_dry_rtn_pkt_per_fam_tdy': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) on reporting date',
    'num_of_dry_rtn_pkt_per_fam_til_now': 'Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.) till now (cumulative)',
    'num_of_fd_pkt_tdy': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. on reporting date',
    'num_of_fd_pkt_til_now': 'Number of food packets Rice, Pulses, potatoes, soyabean, turmeric, etc. till now (cumulative)',
    'num_of_poly_shts_dist_tdy': 'Number of Polythene sheets distributed on reporting date',
    'num_of_poly_shts_dist_til_now': 'Number of Polythene sheets distributed till now (cumulative)',
    'otr_ingts_tdy': 'Other Ingredients (KG/Ltr/number) on reporting date',
    'otr_ingts_til_now': 'Other Ingredients (KG/Ltr/number) till now (cumulative)',
  };

  const STATE_COLUMN_ORDER = Object.values(STATE_FIELD_MAPPING);

  // Create columns
  const columns = STATE_COLUMN_ORDER.map(label => ({ label, key: label }));

  // Transform each record
  const transformedData = records.map((rec) => {
    const transformedRow = {};
    Object.keys(STATE_FIELD_MAPPING).forEach(apiField => {
      const displayName = STATE_FIELD_MAPPING[apiField];

      if (apiField === 'name') {
        // District Name field
        transformedRow[displayName] = rec[apiField] || 'NA';
      } else if (apiField === 'river_name') {
        // River name
        transformedRow[displayName] = rec[apiField] || 'NA';
      } else {
        // All other fields
        let value = rec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';
      }
    });
    return transformedRow;
  });

  // Add TOTAL row
  console.log('🔢 Calculating TOTAL row...');
  const totalRow = {
    'District Name': 'Total'  // ✅ Use 'Total' for State Report
  };

  STATE_COLUMN_ORDER.forEach(columnLabel => {
    // Skip District Name (already set)
    if (columnLabel === 'District Name') {
      return;
    }

    if (columnLabel.includes('Name of major river')) {
      totalRow[columnLabel] = 'NA';
      return;
    }

    // Sum all numeric values
    let sum = 0;
    let hasValidData = false;
    transformedData.forEach(row => {
      const value = row[columnLabel];
      const numValue = (typeof value === 'string' && value !== '-' && value !== 'NA' && !isNaN(parseFloat(value)))
        ? parseFloat(value)
        : (typeof value === 'number' ? value : null);
      if (numValue !== null && !isNaN(numValue)) {
        sum += numValue;
        hasValidData = true;
      }
    });
    totalRow[columnLabel] = hasValidData ? sum.toString() : 'NA';
  });

  transformedData.push(totalRow);
  console.log('✅ TOTAL ROW (Meals):', totalRow['Total number of meals provided till date (Cumulative)']);
  console.log(`✅ Transformed ${transformedData.length} state rows`);

  return { columns, transformedData };
};

// 🚨 FIXED: Flood Form1 Transformer - Handle non-string keys from backend
export const transformFloodForm1DataForExcel = (data, columnOrder) => {
  console.log('transformFloodForm1DataForExcel called', data?.length || 0, columnOrder);

  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn('No Flood Form1 data');
    return [[], []];
  }

  // ✅ EXACT column mapping - Web UI + Backend fields
  const columnMapping = {
    distname: 'District Name',
    totalblockcount: 'Total Blocks',
    affected: 'Affected Blocks',
    partiallyaffected: 'Partially Affected Blocks',
    notaffected: 'Not Affected Blocks',
    totalmarkedblocks: 'Total Marked Blocks'
  };

  // ✅ SAFELY extract keys - handle numbers/objects from backend
  let keys = ['distname', 'totalblockcount', 'affected', 'partiallyaffected', 'notaffected', 'totalmarkedblocks'];
  if (columnOrder && Array.isArray(columnOrder)) {
    keys = columnOrder
      .map(item => {
        // Handle string, number, or object keys from backend
        if (typeof item === 'string') return item;
        if (typeof item === 'number') return String(item);
        if (item && typeof item === 'object' && item.key) return item.key;
        return null;
      })
      .filter(Boolean); // Remove nulls
  }

  // ✅ Create columns with safe string conversion
  const columns = keys.map(key => {
    const safeKey = String(key); // ✅ Convert ANYTHING to string safely
    return {
      label: columnMapping[safeKey] || safeKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      key: safeKey
    };
  });

  console.log('✅ Columns created:', columns.map(c => `${c.key}→${c.label}`).join(', '));

  // ✅ Transform data with fallback for totalmarkedblocks
  const transformedData = data.map(row => {
    const transformedRow = {};
    columns.forEach(col => {
      let value = row[col.key];

      // ✅ Compute totalmarkedblocks if missing (Web UI pattern)
      if (col.key === 'totalmarkedblocks') {
        const affected = parseInt(row.affected) || 0;
        const partially = parseInt(row.partiallyaffected) || 0;
        const not = parseInt(row.notaffected) || 0;
        value = affected + partially + not;
      }

      transformedRow[col.label] = (value !== null && value !== undefined) ? value : '';
    });
    return transformedRow;
  });

  console.log('✅ Generated', transformedData.length, 'rows with Total Marked Blocks populated');
  return [columns, transformedData];
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  formatDate,
  getTimestamp,
  formatDateForMISExcel,
  transformMISDistrictDatewiseData,
  transformMISDistrictData,
  transformMISStateDatewiseData,
  transformMISStateData
};

